import { faPiggyBank, faSparkles } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Tag } from 'antd'
import classNames from 'classnames'
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { MaintenancePlanSavingsBuilderInput, useMaintenancePlanSavings } from '../../hooks/useMaintenancePlanSavings'

export type MaintenancePlanSavingsResult = {
  eligible: boolean
  potentialSavingsUsc?: number
  planDefinitionGuid?: string
  planName?: string
  ctaUrl?: string
}

const formatPotentialSavings = (usc?: number) => {
  if (typeof usc !== 'number') {
    return null
  }

  const dollars = Math.floor(usc / 100)
  const cents = usc % 100

  if (cents === 0) {
    return `$${dollars}`
  }

  return `$${dollars}.${cents.toString().padStart(2, '0')}`
}

interface MaintenancePlanSavingsBannerProps {
  invoiceGuid?: string
  estimateGuid?: string
  className?: string
  variant?: 'banner' | 'inline'
  enabled?: boolean
  builderInput?: MaintenancePlanSavingsBuilderInput
  preferBuilder?: boolean
  unauth?: boolean
  copyVariant?: 'default' | 'estimate'
  onSavingsEvaluated?: (result: MaintenancePlanSavingsResult | null) => void
  suppressVisuals?: boolean
  savingsOverride?: MaintenancePlanSavingsResult & {
    eligible: true
    potentialSavingsUsc: number
    planName: string
  }
}

/**
 * Banner component that displays potential maintenance plan savings
 * Shows when a customer could save money by signing up for a maintenance plan
 */
export const MaintenancePlanSavingsBanner: React.FC<MaintenancePlanSavingsBannerProps> = ({
  invoiceGuid,
  estimateGuid,
  className,
  variant = 'banner',
  enabled = true,
  builderInput,
  preferBuilder = false,
  unauth = false,
  copyVariant = 'default',
  onSavingsEvaluated,
  suppressVisuals = false,
  savingsOverride,
}) => {
  const navigate = useNavigate()

  const shouldQuery = enabled && !savingsOverride

  const { savings, formattedSavings, isLoading } = useMaintenancePlanSavings({
    invoiceGuid,
    estimateGuid,
    enabled: shouldQuery,
    builderInput,
    preferBuilder,
    unauth,
  })

  const resolvedSavings: MaintenancePlanSavingsResult | undefined = savingsOverride ? { ...savingsOverride } : savings

  const resolvedFormattedSavings = useMemo(() => {
    if (savingsOverride) {
      return formatPotentialSavings(savingsOverride.potentialSavingsUsc)
    }
    return formattedSavings
  }, [savingsOverride, formattedSavings])

  const resolvedLoading = savingsOverride ? false : isLoading

  useEffect(() => {
    if (!onSavingsEvaluated) {
      return
    }

    if (!enabled) {
      // biome-ignore lint/suspicious/noConsole: Debug logging for maintenance plan banner
      console.log('[MaintenancePlanSavingsBanner] disabled, sending null results')
      onSavingsEvaluated(null)
      return
    }

    if (savingsOverride) {
      // biome-ignore lint/suspicious/noConsole: Debug logging for maintenance plan banner
      console.log('[MaintenancePlanSavingsBanner] using override results', { savingsOverride })
      onSavingsEvaluated(savingsOverride)
      return
    }

    if (!resolvedLoading) {
      // biome-ignore lint/suspicious/noConsole: Debug logging for maintenance plan banner
      console.log('[MaintenancePlanSavingsBanner] reporting query results', {
        resolvedSavings,
      })
      onSavingsEvaluated(resolvedSavings ?? { eligible: false })
    } else {
      // biome-ignore lint/suspicious/noConsole: Debug logging for maintenance plan banner
      console.log('[MaintenancePlanSavingsBanner] still loading, deferring evaluation')
    }
  }, [enabled, onSavingsEvaluated, resolvedLoading, resolvedSavings, savingsOverride])

  if (!enabled) {
    return null
  }

  // Don't show anything while loading or if not eligible
  if (resolvedLoading || !resolvedSavings || !resolvedSavings.eligible) {
    // biome-ignore lint/suspicious/noConsole: Debug logging for maintenance plan banner
    console.log('[MaintenancePlanSavingsBanner] hidden due to loading/ineligible', {
      resolvedLoading,
      resolvedSavings,
    })
    return null
  }

  if (!resolvedFormattedSavings) {
    // biome-ignore lint/suspicious/noConsole: Debug logging for maintenance plan banner
    console.log('[MaintenancePlanSavingsBanner] missing formatted savings, hiding banner', {
      resolvedSavings,
    })
    return null
  }

  const handleCtaClick = () => {
    if (resolvedSavings?.ctaUrl) {
      window.open(resolvedSavings.ctaUrl, '_blank', 'noopener,noreferrer')
      return
    }

    // Fallback: keep existing in-app route if company has not configured a CTA
    navigate('/maintenance-plans')
  }

  if (variant === 'inline') {
    return (
      <div className={classNames('flex items-center gap-2', className)}>
        <Tag color="green" className="m-0">
          <span className="hidden md:inline">
            <FontAwesomeIcon icon={faPiggyBank} className="mr-1" />
          </span>
          Save {resolvedFormattedSavings} with {resolvedSavings.planName}
        </Tag>
        <Button type="link" size="small" onClick={handleCtaClick} className="p-0">
          Learn More
        </Button>
      </div>
    )
  }

  const bannerHeading =
    copyVariant === 'estimate'
      ? `Save as much as ${resolvedFormattedSavings}`
      : `You could save ${resolvedFormattedSavings}!`

  if (suppressVisuals) {
    return null
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-3 rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-blue-50 p-4',
        'shadow-sm transition-shadow hover:shadow-md',
        'md:flex-row md:items-center md:justify-between',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="hidden rounded-full bg-green-100 p-3 md:flex">
          <FontAwesomeIcon icon={faPiggyBank} className="text-green-600 text-xl" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSparkles} className="text-yellow-500" />
            <h3 className="m-0 text-base font-semibold text-gray-900 md:text-lg">{bannerHeading}</h3>
          </div>
          <p className="m-0 text-sm text-gray-600">
            Sign up for our {resolvedSavings.planName} and enjoy exclusive member pricing and discounts.
          </p>
        </div>
      </div>
      <Button
        type="primary"
        size="large"
        onClick={handleCtaClick}
        className="w-full bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700 md:w-auto"
      >
        Sign Up & Save
      </Button>
    </div>
  )
}

MaintenancePlanSavingsBanner.displayName = 'MaintenancePlanSavingsBanner'

/**
 * Compact version for displaying in tight spaces
 */
export const MaintenancePlanSavingsTag: React.FC<{
  invoiceGuid?: string
  estimateGuid?: string
  unauth?: boolean
}> = ({ invoiceGuid, estimateGuid, unauth = false }) => {
  const { savings, formattedSavings, isLoading } = useMaintenancePlanSavings({
    invoiceGuid,
    estimateGuid,
    unauth,
  })

  if (isLoading || !savings || !savings.eligible) {
    return null
  }

  return (
    <Tag color="success" className="m-0">
      <span className="hidden md:inline">
        <FontAwesomeIcon icon={faPiggyBank} className="mr-1" />
      </span>
      Save {formattedSavings}
    </Tag>
  )
}

MaintenancePlanSavingsTag.displayName = 'MaintenancePlanSavingsTag'
